import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Loader2, RotateCcw, Search, UserPlus } from "lucide-react";
import { api } from "../../api";
import { DESK } from "../../constants/testIds";
import { useDeskData } from "../../contexts/DeskDataContext";
import { hasPermission, isPhoneQuery } from "../../lib/format";
import { MEMBER_STATUS_FILTERS, membershipStatusPillProps } from "../../lib/memberStatus";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { GymLoader } from "../../components/common/GymLoader";
import { StatusPill } from "../../components/common/StatusPill";

// UX-FD-01 §1 SCR-P02-11: fast member lookup by name or phone. Shows the FULL member list by
// default (All/Active/Frozen/Expired tabs, same taxonomy + `GET /reports/members-by-status`
// read-model as the Owner Cockpit's Members page — see `lib/memberStatus.js`) instead of a blank
// screen waiting for a search — Manager/Front Desk both have `member.view` so both see it
// identically. Search still works within whichever tab is active; deliberately never excludes
// lapsed/inactive members — SCHEMA.md never soft-deletes a member record — so a lapsed member
// always surfaces with a "Reactivate" action instead of ever looking like they don't exist.
export default function MemberLookup() {
  const { me } = useDeskData();
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const timerRef = useRef(null);

  const filters = useMemo(() => [
    ...MEMBER_STATUS_FILTERS,
    ...(hasPermission(me, "pnl.view") ? [{ key: "dues", label: "Dues outstanding" }] : []),
    ...(hasPermission(me, "retention.view") ? [{ key: "at_risk", label: "At-risk" }] : []),
  ], [me]);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (query.trim().length < 2) { setSubmitted(""); return; }
    timerRef.current = setTimeout(() => setSubmitted(query.trim()), 250);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  const membersQ = useQuery({
    queryKey: ["desk-members-by-status", filter, submitted],
    queryFn: async () => {
      const searchParam = submitted ? (isPhoneQuery(submitted) ? { phone: submitted.replace(/[\s-]/g, "") } : { name: submitted }) : {};
      const statusParam = filter === "all" ? {} : { status: filter };
      return (await api.get("/reports/members-by-status", { params: { ...searchParam, ...statusParam, limit: 200 } })).data.members;
    },
    enabled: filter !== "at_risk" && filter !== "dues",
  });

  const atRiskQ = useQuery({
    queryKey: ["desk-retention-at-risk"],
    queryFn: async () => (await api.get("/retention/at-risk")).data.members || [],
    enabled: filter === "at_risk",
  });

  const duesQ = useQuery({
    queryKey: ["desk-dues-outstanding"],
    queryFn: async () => (await api.get("/reports/dues-outstanding")).data.members || [],
    enabled: filter === "dues",
  });

  const rows = useMemo(() => {
    if (filter === "at_risk") {
      return (atRiskQ.data || []).map((r) => ({ id: r.member_ref, name: r.member_name, phone: null, membershipStatus: "active", reason: r.reason }));
    }
    if (filter === "dues") {
      return (duesQ.data || []).map((r) => ({ id: r.member_ref, name: r.member_name, phone: r.phone, membershipStatus: r.status === "active" ? "active" : "none" }));
    }
    return (membersQ.data || []).map((m) => ({ id: m.id, name: m.name, phone: m.phone, membershipStatus: m.membership_status, accountStatus: m.account_status }));
  }, [filter, membersQ.data, atRiskQ.data, duesQ.data]);

  const isLoading = filter === "at_risk" ? atRiskQ.isLoading : filter === "dues" ? duesQ.isLoading : membersQ.isLoading;

  return (
    <div data-testid={DESK.memberLookupPage} className="space-y-6">
      <motion.div {...fadeUp(reducedMotion)} className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Members</h1>
          <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Every member, lapsed ones included — with a Reactivate option.</p>
        </div>
        <Button data-testid={DESK.newMemberButton} onClick={() => navigate("/desk/members/new")} className="text-white gap-2" style={{ background: "#D6202B" }}>
          <UserPlus size={15} /> New member
        </Button>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.03)} className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1.5 flex-wrap">
          {filters.map((f) => (
            <button
              key={f.key}
              data-testid={DESK.memberFilterTab(f.key)}
              onClick={() => setFilter(f.key)}
              className="text-sm font-medium rounded-[10px] px-3.5 py-1.5 transition-colors"
              style={filter === f.key
                ? { background: "#D6202B", color: "#fff" }
                : { background: "transparent", border: "1px solid #EBEAE5", color: "#68686E" }}
            >
              {f.label}
            </button>
          ))}
        </div>
        {filter !== "at_risk" && filter !== "dues" && (
          <div className="relative max-w-sm w-full">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#68686E" }} />
            <Input
              data-testid={DESK.memberSearchInput}
              placeholder="Search name or phone number…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        )}
      </motion.div>

      {isLoading && <GymLoader label="Loading members…" />}

      {!isLoading && rows.length === 0 && (
        <p className="text-sm" data-testid={DESK.memberEmptyState} style={{ color: "#68686E" }}>
          {filter === "at_risk" ? "No at-risk members flagged right now." : filter === "dues" ? "No members owe outstanding dues right now." : submitted ? `No member found for "${submitted}".` : `No ${filter === "all" ? "" : filter + " "}members yet. Use New member to add one.`}
        </p>
      )}

      {rows.length > 0 && (
        <div className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          {rows.map((m) => (
            <div key={m.id} data-testid={DESK.memberRow(m.id)} className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid #EBEAE5" }}>
              <div>
                <p className="font-semibold text-sm" style={{ color: "#141416" }}>{m.name}</p>
                <p className="text-xs" style={{ color: "#68686E" }}>{filter === "at_risk" ? m.reason : m.phone}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusPill {...membershipStatusPillProps(m.membershipStatus)} />
                {m.accountStatus && m.accountStatus !== "active" && (
                  <Button
                    size="sm" variant="outline" data-testid={DESK.memberReactivateButton(m.id)}
                    onClick={() => navigate(`/desk/members/${m.id}?reactivate=1`)}
                    className="gap-1"
                  >
                    <RotateCcw size={13} /> Reactivate
                  </Button>
                )}
                <Button size="sm" data-testid={DESK.memberViewButton(m.id)} onClick={() => navigate(`/desk/members/${m.id}`)} className="text-white" style={{ background: "#D6202B" }}>
                  View
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
